import { useState } from 'react';

import { useCartLiveValidationActions } from 'features/cart/hooks/use-cart-live-validation-actions';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';
import { Typography } from 'ucl';
import { Icon } from 'ucl/components/icon/Icon';

import ConfirmDialog from 'components/confirm-dialog';
import { t } from 'state/intl/translations';
import { getServiceModeFromLS } from 'state/service-mode';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { logCartItemRemoved, logCartRemoveFromCart } from '../../utils/cart-analytics';

import { cartActionsStyles } from './cart-actions-styles';
import { CartEntryActionButton } from './cart-entry-action-button';

export const CartEntryDeleteButton = ({
  cartEntry,
  variant,
  disabled,
}: {
  cartEntry: ICartEntry;
  variant: 'textOnly' | 'iconOnly';
  disabled?: boolean;
}) => {
  const { cartEntries, removeItemFromMinimalCart } = useMinimalCart();
  const { requestCartValidation } = useCartLiveValidationActions();
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

  const handleRemoveItem = () => {
    hapticNotification({ type: HapticsNotificationType.WARNING });
    setShowDeleteConfirmationDialog(true);
  };

  const handleConfirmRemoveItem = () => {
    setShowDeleteConfirmationDialog(false);
    logCartItemRemoved();
    const serviceMode = getServiceModeFromLS();
    if (serviceMode) {
      logCartRemoveFromCart({
        cartEntry,
        previousCartEntries: cartEntries,
        serviceMode,
        isReward: cartEntry.type === 'reward',
      });
    }
    removeItemFromMinimalCart(cartEntry);
    requestCartValidation();
  };

  return (
    <>
      {variant === 'textOnly' && (
        <CartEntryActionButton
          accessibilityLabel={`${t('remove')} ${cartEntry.name} `}
          testID="remove-cart-entry-button"
          onPress={handleRemoveItem}
          disabled={disabled}
        >
          <Typography style={cartActionsStyles.removeText}>{t('remove')}</Typography>
        </CartEntryActionButton>
      )}
      {variant === 'iconOnly' && (
        <CartEntryActionButton
          accessibilityLabel={`${t('remove')} ${cartEntry.name} `}
          testID="remove-cart-entry-button"
          onPress={handleRemoveItem}
          icon={<Icon variant="bkRemove" size="md" />}
          disabled={disabled}
        />
      )}
      <ConfirmDialog
        showDialog={showDeleteConfirmationDialog}
        heading={t('removeItem')}
        body={t('removeItemFromCart', {
          item: cartEntry.name,
          offerText: '',
        })}
        confirmLabel={t('okay')}
        cancelLabel={t('cancel')}
        onConfirm={handleConfirmRemoveItem}
        onCancel={() => {
          setShowDeleteConfirmationDialog(false);
        }}
        modalAppearanceEventMessage={''}
      />
    </>
  );
};
